import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "./utils";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const dashboardApi = createApi({
  reducerPath: "dashboard",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: ["Dashboard"],
  endpoints: (builder) => ({
    // --- Admin ---
    getAdminStats: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/admin/dashboard/admin-stats", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getAdminTokenTrend: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/admin/dashboard/token-trend", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getAdminTokenBreakdown: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/admin/dashboard/token-breakdown", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getAdminClinicRanking: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/admin/dashboard/clinic-ranking", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getAdminPatientEcosystem: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/admin/dashboard/patient-ecosystem", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getAdminSystemHealth: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/admin/dashboard/system-health", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getAdminChurnRisk: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/admin/dashboard/churn-risk", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getAdminCompliance: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/admin/dashboard/compliance", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getAdminActivity: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/admin/dashboard/activity", "POST");
      },
      providesTags: ["Dashboard"],
    }),

    // --- Clinic ---
    getClinicStats: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/clinic/stats", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getClinicToday: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/clinic/dashboard/clinic/today", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getClinicWeekly: builder.query({
      query: ({ token }) => {
        return apiFunction({ token }, "/api/clinic/dashboard/clinic/weekly", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getClinicMonthlyAppointments: builder.query({
      query: ({ token, months }) => {
        return apiFunction({ token, months }, "/api/clinic/dashboard/clinic/monthly-appointments", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getClinicActivity: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/clinic/activity", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getClinicStaffActivity: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/clinic/staff-activity", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getClinicNotifications: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/clinic/notifications", "POST");
      },
      providesTags: ["Dashboard"],
    }),

    // --- Performance ---
    getPerformanceStaff: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/performance/staff", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getPerformanceAppointments: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/performance/appointments", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getPerformanceTreatments: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/performance/treatments", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getPerformanceCommunication: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/performance/communication", "POST");
      },
      providesTags: ["Dashboard"],
    }),
    getPerformanceFinancial: builder.query({
      query: ({ token, period }) => {
        return apiFunction({ token, period }, "/api/clinic/dashboard/performance/financial", "POST");
      },
      providesTags: ["Dashboard"],
    }),
  }),
});

export const {
  // Admin
  useGetAdminStatsQuery,
  useLazyGetAdminStatsQuery,
  useGetAdminTokenTrendQuery,
  useLazyGetAdminTokenTrendQuery,
  useGetAdminTokenBreakdownQuery,
  useLazyGetAdminTokenBreakdownQuery,
  useGetAdminClinicRankingQuery,
  useLazyGetAdminClinicRankingQuery,
  useGetAdminPatientEcosystemQuery,
  useLazyGetAdminPatientEcosystemQuery,
  useGetAdminSystemHealthQuery,
  useLazyGetAdminSystemHealthQuery,
  useGetAdminChurnRiskQuery,
  useLazyGetAdminChurnRiskQuery,
  useGetAdminComplianceQuery,
  useLazyGetAdminComplianceQuery,
  useGetAdminActivityQuery,
  useLazyGetAdminActivityQuery,
  // Clinic
  useGetClinicStatsQuery,
  useLazyGetClinicStatsQuery,
  useGetClinicTodayQuery,
  useLazyGetClinicTodayQuery,
  useGetClinicWeeklyQuery,
  useLazyGetClinicWeeklyQuery,
  useGetClinicMonthlyAppointmentsQuery,
  useLazyGetClinicMonthlyAppointmentsQuery,
  useGetClinicActivityQuery,
  useLazyGetClinicActivityQuery,
  useGetClinicStaffActivityQuery,
  useLazyGetClinicStaffActivityQuery,
  useGetClinicNotificationsQuery,
  useLazyGetClinicNotificationsQuery,
  // Performance
  useGetPerformanceStaffQuery,
  useLazyGetPerformanceStaffQuery,
  useGetPerformanceAppointmentsQuery,
  useLazyGetPerformanceAppointmentsQuery,
  useGetPerformanceTreatmentsQuery,
  useLazyGetPerformanceTreatmentsQuery,
  useGetPerformanceCommunicationQuery,
  useLazyGetPerformanceCommunicationQuery,
  useGetPerformanceFinancialQuery,
  useLazyGetPerformanceFinancialQuery,
} = dashboardApi;
